import type { Call, CallingMe, CallMode, CallOutcome, CallPurpose } from '@/types/api/calling'

/** `3m 12s`, `45s`; null when unknown. */
export function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined) return null
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  return minutes > 0 ? `${minutes}m ${rest}s` : `${rest}s`
}

/** `01:23` for the live timer. */
export function formatTimer(seconds: number): string {
  const safe = Math.max(0, Math.floor(seconds))
  const minutes = Math.floor(safe / 60)
  return `${String(minutes).padStart(2, '0')}:${String(safe % 60).padStart(2, '0')}`
}

export function outcomeLabel(outcome: CallOutcome | null, direction: Call['direction'] = 'OUTBOUND'): string {
  if (!outcome) return 'In progress'
  if (direction === 'INBOUND' && outcome === 'NO_ANSWER') return 'Missed'
  switch (outcome) {
    case 'ANSWERED': return 'Answered'
    case 'NO_ANSWER': return 'No answer'
    case 'BUSY': return 'Busy'
    case 'FAILED': return 'Failed'
    case 'CANCELED': return 'Canceled'
    case 'AGENT_UNAVAILABLE': return "You didn't answer"
    case 'NOT_CONNECTED': return 'Not connected'
    case 'UNKNOWN': return 'Result unknown'
  }
}

/** Tailwind classes for an outcome badge. */
export function outcomeTone(outcome: CallOutcome | null): string {
  switch (outcome) {
    case 'ANSWERED': return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    case null: return 'bg-sky-50 text-sky-700 border-sky-200'
    case 'FAILED':
    case 'AGENT_UNAVAILABLE': return 'bg-rose-50 text-rose-700 border-rose-200'
    default: return 'bg-amber-50 text-amber-700 border-amber-200'
  }
}

/** What the dock says about a call right now. `nowMs` drives the connected timer. */
export function callStatusText(call: Call, nowMs: number): string {
  const sim = call.providerType === 'MOBILE_SIM'
  switch (call.status) {
    case 'INITIATED':
      if (sim) return call.pushSentAt ? 'Waiting for your phone…' : 'Opening your dialer…'
      return 'Starting call…'
    case 'DIALING':
      if (call.direction === 'INBOUND') return 'Incoming call…'
      return sim ? 'Calling from your phone…' : 'Ringing your phone…'
    case 'CONNECTED': {
      const since = call.answeredAt ? Date.parse(call.answeredAt) : nowMs
      return `Connected ${formatTimer((nowMs - since) / 1000)}`
    }
    case 'ENDED': {
      const duration = call.outcome === 'ANSWERED' ? formatDuration(call.connectedDurationSec) : null
      return `Ended · ${outcomeLabel(call.outcome, call.direction)}${duration ? ` · ${duration}` : ''}`
    }
  }
}

export interface CallOption {
  mode: CallMode
  purpose?: CallPurpose
  label: string
}

/**
 * The call choices a PHONE value offers, or none when the button must stay hidden (no calling on
 * the plan, no call:make, or no usable mode). Exotel shows one entry per purpose when the workspace
 * has both caller IDs; the user's preferred mode comes first.
 */
export function callOptions(me: CallingMe | undefined, opts: { native: boolean }): CallOption[] {
  if (!me || !me.featureEnabled || !me.canMakeCalls) return []
  const options: CallOption[] = []
  if (me.modes.includes('CLICK_TO_CALL')) {
    if (me.purposes.length > 1) {
      options.push({ mode: 'CLICK_TO_CALL', purpose: 'SERVICE', label: 'Call via Exotel · Service' })
      options.push({ mode: 'CLICK_TO_CALL', purpose: 'PROMOTIONAL', label: 'Call via Exotel · Promotional' })
    } else {
      options.push({ mode: 'CLICK_TO_CALL', label: 'Call via Exotel' })
    }
  }
  // The app dials on its own SIM whenever this phone is registered; browsers need a push-capable phone.
  if (me.modes.includes('SIM') || (opts.native && me.devices.length > 0)) {
    options.push({ mode: 'SIM', label: opts.native ? 'Call from this phone' : 'Call from my phone' })
  }
  const preferred = me.profile?.preferredMode
  return preferred ? [...options].sort((a, b) => Number(b.mode === preferred) - Number(a.mode === preferred)) : options
}

export const PROVIDER_BADGE: Record<Call['trackingSource'], string> = {
  CARRIER: 'Carrier-tracked',
  DEVICE_LOG: 'Phone log',
}
